import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, apiProblemOf, isStaleWrite } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { PartyStepper } from "../booking/components/PartyStepper";
import { SlotList } from "../booking/components/SlotList";
import type { SelectedSlot } from "../booking/flow/state";
import { formatServiceDate, formatSlotTime, todayInTz } from "../booking/format";
import {
  cancelBooking,
  modifyBooking,
  useManageBooking,
  useOpenSlots,
  type ManageBooking,
} from "../booking/publicApi";
import { ShellFrame } from "../shell/ShellFrame";
import { ErrorState, LoadingState } from "../shell/states";

/** The stepper cap; the server enforces the real per-service max and returns LARGE_PARTY_REDIRECT above it. */
const PARTY_CAP = 20;

/**
 * `/m/:venueId/:token` — the no-login manage-booking page the confirmation/reminder link opens (BK-12).
 * It resolves the booking by its manage token (venue-scoped), shows FACTS ONLY (never contact PII), and
 * lets the diner MODIFY (party/date/time re-pick via the same availability search, BK-4) or CANCEL
 * within the venue's canonical cutoff (BK-6) — the disabled state + the cutoff/policy are rendered from
 * the SAME server verdict the write path enforces. FR/EN via @repo/i18n; times are venue-local.
 */
export function ManagePage() {
  const { venueId = "", token = "" } = useParams();
  const { t } = useTranslation();
  const query = useManageBooking(venueId, token);
  const [booking, setBooking] = useState<ManageBooking | null>(null);

  // Mirror the query result into editable local state (overwritten locally on modify) — adjusted
  // during render (comparing against the last-seen query.data reference) rather than via an effect.
  const [lastQueryData, setLastQueryData] = useState(query.data);
  if (query.data !== lastQueryData) {
    setLastQueryData(query.data);
    if (query.data) setBooking(query.data);
  }

  const notFound = query.isError && query.error instanceof ApiError && query.error.status === 404;

  const body = (() => {
    if (booking) {
      return (
        <ManageBody
          booking={booking}
          venueId={venueId}
          token={token}
          onUpdated={setBooking}
          onReload={() => void query.refetch()}
        />
      );
    }
    if (query.isLoading) return <LoadingState />;
    if (notFound) {
      return (
        <section aria-labelledby="manage-nf" data-testid="manage-not-found">
          <h2 id="manage-nf">{t("widget.manage.notFound.title")}</h2>
          <p>{t("widget.manage.notFound.body")}</p>
        </section>
      );
    }
    return <ErrorState onRetry={() => void query.refetch()} />;
  })();

  return <ShellFrame venueName={booking?.venueName}>{body}</ShellFrame>;
}

type Mode = "view" | "modify" | "confirm-cancel" | "cancelled";

function ManageBody({
  booking,
  venueId,
  token,
  onUpdated,
  onReload,
}: {
  booking: ManageBooking;
  venueId: string;
  token: string;
  onUpdated: (b: ManageBooking) => void;
  onReload: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language;
  const tz = booking.timeZone;

  const [mode, setMode] = useState<Mode>("view");
  const [party, setParty] = useState(booking.partySize);
  const [date, setDate] = useState(booking.serviceDay);
  const [picked, setPicked] = useState<SelectedSlot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const slots = useOpenSlots(venueId, date, party, mode === "modify");

  // Focus management for the inline confirm-cancel step (features/accessibility.md — manage/cancel
  // focus). The step swaps the whole `<main>` (no overlay, so there is nothing else to trap focus AT),
  // so accessibility is delivered by moving focus INTO the confirm heading when it opens and RETURNING
  // it to the triggering "Annuler" control on Escape/keep — the modal-dialog contract, minus the trap.
  const cancelTriggerRef = useRef<HTMLButtonElement>(null);
  const confirmHeadingRef = useRef<HTMLHeadingElement>(null);
  const restoreFocus = useRef(false);

  const keepBooking = useCallback(() => {
    setError(null);
    setMode("view"); // the effect returns focus to the cancel trigger
  }, []);

  useEffect(() => {
    if (mode === "confirm-cancel") {
      confirmHeadingRef.current?.focus();
      // Escape closes the inline confirm step like a modal (listener, not a handler on the non-
      // interactive <section>, so it stays valid HTML and the keyboard contract still holds).
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape" && !busy) keepBooking();
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }
    if (mode === "view" && restoreFocus.current) {
      restoreFocus.current = false;
      cancelTriggerRef.current?.focus();
    }
    return undefined;
  }, [mode, busy, keepBooking]);

  function openConfirmCancel() {
    restoreFocus.current = true;
    setMode("confirm-cancel");
  }

  const statusLabel = t(`widget.manage.status.${booking.status}` as const, {
    defaultValue: booking.status,
  });

  async function doCancel() {
    setBusy(true);
    setError(null);
    try {
      const updated = await cancelBooking(venueId, token, booking.version);
      onUpdated(updated);
      setMode("cancelled");
    } catch (err) {
      handleWriteError(err, "cancel");
    } finally {
      setBusy(false);
    }
  }

  async function doModify() {
    setBusy(true);
    setError(null);
    try {
      const result = await modifyBooking(venueId, token, {
        expectedVersion: booking.version,
        idempotencyKey: `modify-${booking.bookingId}-${Date.now()}`,
        partySize: party !== booking.partySize ? party : undefined,
        startSec: picked?.startSec,
        serviceId: picked?.serviceId,
      });
      if (result.kind === "resubmitted") {
        navigate(
          `/m/${result.redirect.venueId}/${encodeURIComponent(result.redirect.manageToken)}`,
        );
        return;
      }
      onUpdated(result.booking);
      resetModify(result.booking);
      setMode("view");
    } catch (err) {
      handleWriteError(err, "modify");
    } finally {
      setBusy(false);
    }
  }

  function handleWriteError(err: unknown, kind: "modify" | "cancel") {
    if (isStaleWrite(err)) {
      setError(t("widget.manage.error.stale"));
      onReload();
      setMode("view");
      return;
    }
    const code = (apiProblemOf(err) as { rejectionCode?: string } | undefined)?.rejectionCode;
    if (code) {
      setError(
        t(`widget.manage.reject.${code}`, { defaultValue: t("widget.manage.reject.generic") }),
      );
      return;
    }
    setError(t(kind === "modify" ? "widget.manage.error.modify" : "widget.manage.error.cancel"));
  }

  function resetModify(b: ManageBooking) {
    setParty(b.partySize);
    setDate(b.serviceDay);
    setPicked(null);
  }

  const changed = party !== booking.partySize || picked !== null;

  if (mode === "cancelled") {
    return (
      <section aria-labelledby="manage-cancelled" data-testid="manage-cancelled">
        <h2 id="manage-cancelled">{t("widget.manage.cancelled.title")}</h2>
        <p>{t("widget.manage.cancelled.body")}</p>
      </section>
    );
  }

  if (mode === "confirm-cancel") {
    return (
      <section aria-labelledby="manage-cc" data-testid="manage-confirm-cancel">
        <h2 id="manage-cc" ref={confirmHeadingRef} tabIndex={-1} style={{ outline: "none" }}>
          {t("widget.manage.cancel.confirmTitle")}
        </h2>
        <p>{t("widget.manage.cancel.confirmBody")}</p>
        {error ? (
          <p className="rsv-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="rsv-actions">
          <button
            type="button"
            className="rsv-btn-danger"
            disabled={busy}
            onClick={() => void doCancel()}
          >
            {t("widget.manage.actions.confirmCancel")}
          </button>
          <button type="button" className="rsv-btn-ghost" disabled={busy} onClick={keepBooking}>
            {t("widget.manage.actions.keep")}
          </button>
        </div>
      </section>
    );
  }

  if (mode === "modify") {
    return (
      <section aria-labelledby="manage-modify" data-testid="manage-modify">
        <h2 id="manage-modify">{t("widget.manage.modify.title")}</h2>
        {error ? (
          <p className="rsv-error" role="alert">
            {error}
          </p>
        ) : null}

        <label className="rsv-field">
          <span className="rsv-label">{t("widget.manage.modify.date")}</span>
          <input
            type="date"
            className="rsv-input"
            value={date}
            min={todayInTz(tz)}
            onChange={(e) => {
              setDate(e.target.value);
              setPicked(null);
            }}
          />
        </label>

        <PartyStepper
          value={party}
          max={PARTY_CAP}
          labelId="manage-party"
          onChange={(next) => {
            setParty(next);
            setPicked(null);
          }}
        />

        <h3 id="manage-slots">{t("widget.manage.modify.pickTime")}</h3>
        {slots.isLoading ? <LoadingState /> : null}
        {slots.data && slots.data.slots.length > 0 ? (
          <SlotList
            slots={slots.data.slots}
            onPick={setPicked}
            timeZone={tz}
            locale={locale}
            busyStartSec={picked?.startSec ?? null}
            disabled={busy}
            labelledBy="manage-slots"
          />
        ) : slots.data ? (
          <p>{t("widget.manage.modify.noSlots")}</p>
        ) : null}

        <div className="rsv-actions">
          <button
            type="button"
            className="rsv-btn"
            disabled={busy || !changed}
            onClick={() => void doModify()}
          >
            {t("widget.manage.actions.saveChange")}
          </button>
          <button
            type="button"
            className="rsv-btn-ghost"
            disabled={busy}
            onClick={() => {
              resetModify(booking);
              setError(null);
              setMode("view");
            }}
          >
            {t("widget.manage.actions.back")}
          </button>
        </div>
      </section>
    );
  }

  // mode === "view"
  return (
    <section aria-labelledby="manage-title" data-testid="manage-view">
      <h2 id="manage-title">{t("widget.manage.title")}</h2>
      {error ? (
        <p className="rsv-error" role="alert">
          {error}
        </p>
      ) : null}

      <dl className="rsv-summary" data-testid="manage-summary">
        <dt>{t("widget.manage.summary.venue")}</dt>
        <dd>{booking.venueName}</dd>
        <dt>{t("widget.manage.summary.date")}</dt>
        <dd>{formatServiceDate(booking.serviceDay, tz, locale)}</dd>
        <dt>{t("widget.manage.summary.time")}</dt>
        <dd data-testid="manage-time">{formatSlotTime(booking.startSec, tz, locale)}</dd>
        <dt>{t("widget.manage.summary.party")}</dt>
        <dd>{t("widget.manage.summary.partyValue", { guests: booking.partySize })}</dd>
        <dt>{t("widget.manage.status.label")}</dt>
        <dd data-testid="manage-status">{statusLabel}</dd>
      </dl>

      {booking.past ? <p data-testid="manage-past">{t("widget.manage.past")}</p> : null}

      {!booking.withinCancellationWindow && !booking.past && !isTerminal(booking.status) ? (
        <div className="rsv-notice" data-testid="manage-cutoff-closed">
          <p>{t("widget.manage.cutoff.closed")}</p>
          {typeof booking.cancellableUntilSec === "number" ? (
            <p>
              {t("widget.manage.cutoff.until", {
                time: formatSlotTime(booking.cancellableUntilSec, tz, locale),
              })}
            </p>
          ) : null}
          {booking.policyText ? <p>{booking.policyText}</p> : null}
        </div>
      ) : null}

      {booking.allowModify || booking.allowCancel ? (
        <div className="rsv-actions">
          {booking.allowModify ? (
            <button type="button" className="rsv-btn" onClick={() => setMode("modify")}>
              {t("widget.manage.actions.modify")}
            </button>
          ) : null}
          {booking.allowCancel ? (
            <button
              type="button"
              className="rsv-btn-danger"
              data-testid="manage-cancel"
              ref={cancelTriggerRef}
              onClick={openConfirmCancel}
            >
              {t("widget.manage.actions.cancel")}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function isTerminal(status: string): boolean {
  return status === "CANCELLED" || status === "COMPLETED" || status === "NO_SHOW";
}
